import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

import Home from './pages/page-home.js';
import CheckLogin from './components/check-login.js';
import RouteLoadingFallback from './components/common/RouteLoadingFallback.js';
import NotFoundPage from './pages/page-not-found.jsx';

// Keep the home route in the entry bundle so the primary landing path does not
// gain another request. Every other screen owns its code and styles and is
// downloaded only when that route is visited.
const Community = lazy(() => import('./pages/page-community.js'));
const Select = lazy(() => import('./pages/page-select.js'));
const Explore = lazy(() => import('./pages/page-explore.js'));
const Post = lazy(() => import('./pages/page-post.js'));
const Newpost = lazy(() => import('./pages/page-newpost.js'));
const Editpost = lazy(() => import('./pages/page-editpost.js'));
const MyPost = lazy(() => import('./pages/page-mypost.js'));
const MyPostPost = lazy(() => import('./pages/page-mypost-post.js'));
const MyPostLocal = lazy(() => import('./pages/page-mypost-local.js'));
const Synapse = lazy(() => import('./pages/page-synapse.js'));
const Starmap = lazy(() => import('./pages/page-starmap.js'));
const Gig = lazy(() => import('./pages/page-gig.js'));
const GigNewSpace = lazy(() => import('./pages/page-gig-new-space.js'));
const GigExplore = lazy(() => import('./pages/page-gig-explore.js'));
const GigSpace = lazy(() => import('./pages/page-gig-space.js'));
const GigSpaceHome = lazy(() => import('./pages/page-gig-space-home.js'));
const GigSpaceSettings = lazy(() => import('./pages/page-gig-space-settings.js'));
const GigSpaceTeam = lazy(() => import('./pages/page-gig-space-team.js'));
const GigSpaceChat = lazy(() => import('./pages/page-gig-space-chat.js'));
const GigSpacePosts = lazy(() => import('./pages/page-gig-space-posts.js'));
const GigSpaceIntroEdit = lazy(() => import('./pages/page-gig-space-intro-edit.js'));
const GigSpaceBudget = lazy(() => import('./pages/page-gig-space-budget.js'));
const GigPlan = lazy(() => import('./pages/page-gig-plan.js'));
const GroupStorage = lazy(() => import('./pages/page-group-storage.js'));
const Profile = lazy(() => import('./pages/page-profile.js'));
const Editprofile = lazy(() => import('./pages/page-editprofile.js'));
const Drive = lazy(() => import('./pages/page-drive.js'));
const PageSettings = lazy(() => import('./pages/page-settings.js'));
const Newcatag = lazy(() => import('./pages/page-newcatag.js'));
const SearchResult = lazy(() => import('./pages/page-search-result.js'));
const Chat = lazy(() => import('./pages/page-chat.js'));
const Notice = lazy(() => import('./pages/page-notice.js'));
const AstrantisIntro = lazy(() => import('./pages/page-astrantis-intro.js'));
const Operation = lazy(() => import('./pages/page-operation.js'));
const CategoryRequestsAdmin = lazy(() => import('./pages/page-category-requests-admin.js'));
const Credits = lazy(() => import('./pages/page-credits.js'));
const OAuthCallback = lazy(() => import('./components/OAuthCallback.js'));
const Page2FAVerify = lazy(() => import('./pages/page-2fa-verify.js'));
const PrivacyPolicyPage = lazy(() => import('./pages/page-privacy-police.jsx'));
const TermsOfServicePage = lazy(() => import('./pages/page-terms-of-service.jsx'));
const Notice1 = lazy(() => import('./pages/notice/notice-1.js'));
const Notice2 = lazy(() => import('./pages/notice/notice-2.js'));
const Notice3 = lazy(() => import('./pages/notice/notice-3.js'));
const Test = lazy(() => import('./pages/page-test.js'));

function MccScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        const mcc = document.getElementById('main-contents-container');
        if (mcc) mcc.scrollTop = 0;
    }, [pathname]);

    return null;
}

function MCC() {
    return(
        <>
        <MccScrollToTop />
        <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
            {/* 공개 페이지 - 로그인 없이 접근 가능 */}
            <Route path="/" element={<Home />}/>
            <Route path="/index.html" element={<Home />}/>
            <Route path="/test" element={<Test />}/>
            
            {/* Notice 페이지 */}
            <Route path="/notice/1" element={<Notice1 />}/>
            <Route path="/notice/2" element={<Notice2 />}/>
            <Route path="/notice/3" element={<Notice3 />}/>
            <Route path="/intro" element={<AstrantisIntro />}/>
            
            {/* 로그인 관련 페이지 - 팝업으로 처리됨 */}
            <Route path="/oauth/callback" element={<OAuthCallback />}/>
            <Route path="/2fa-verify" element={<Page2FAVerify />}/>
            
            {/* Legal pages - publicly accessible */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            
            {/* 보호된 페이지 - 로그인 필요 */}
            <Route path="/community" element={<Community />}/>
            <Route path="/select" element={<Select />}/>
            <Route path="/explore" element={<Explore />}/>
                <Route path="/post" element={<Post />}/>
                <Route path="/newpost" element={<Newpost />}/>
                <Route path="/editpost" element={<Editpost />}/>
            <Route path="/mypost" element={<MyPost />}/>
                <Route path="/mypost-post" element={<MyPostPost />}/>
                <Route path="/mypost-local" element={<MyPostLocal />}/>
            <Route path="/gig" element={<Gig />}/>
            <Route path="/gig/new" element={<GigNewSpace />}/>
            <Route path="/gig/explore" element={<GigExplore />}/>
                <Route path="/gig/space/:spaceId" element={<GigSpace />}>
                    <Route index element={<GigSpaceHome />}/>
                    <Route path="settings" element={<GigSpaceSettings />}/>
                    <Route path="team" element={<GigSpaceTeam />}/>
                    <Route path="chat" element={<GigSpaceChat />}/>
                    <Route path="storage" element={<GroupStorage />}/>
                    <Route path="posts" element={<GigSpacePosts />}/>
                    <Route path="intro/edit" element={<GigSpaceIntroEdit />}/>
                    <Route path="budget" element={<GigSpaceBudget />}/>
                    <Route path="project/:projectId/plan/:planId" element={<GigPlan />}/>
                </Route>
            <Route path="/profile" element={<Profile />}/>
                <Route path="/editprofile" element={<Editprofile />}/>
            <Route path="/drive" element={<Drive />}/>
            <Route path="/group/:groupId/storage" element={<GroupStorage />}/>
            <Route path="/settings" element={<PageSettings />}/>
                <Route path="/newcatag" element={<Newcatag />}/>
            <Route path="/synapse" element={<Synapse />}/>
            <Route path="/starmap" element={<Starmap />}/>
            <Route path="/chat" element={<Chat />}/>
            <Route path="/notice" element={<Notice />}/>
            <Route path="/operation" element={<Operation />}/>
            <Route path="/operation/category-requests" element={<CategoryRequestsAdmin />}/>
            <Route path="/credits" element={<Credits />}/>
            <Route path="/search-result" element={<SearchResult />}/>
            
            {/* 404 page - MCC 내부의 정의되지 않은 경로 */}
            <Route path="*" element={<NotFoundPage />}/>
        </Routes>
        </Suspense>

        <Routes>
            <Route path="*" element={<CheckLogin />}/>
            <Route path="/" element={''}/>
            <Route path="/test" element={''} />
            <Route path="/oauth/callback" element={''} />
            <Route path="/2fa-verify" element={''} />
            <Route path="/privacy-policy" element={''} />
            <Route path="/terms-of-service" element={''} />
            <Route path="/intro" element={''} />
            {/* /post 경로는 공개 게시글 조회를 위해 예외 처리(google에서 크롤링 가능하도록) (sitemap.xml 포함) */}
            <Route path="/post" element={''} />
        </Routes>
        </>
    );
}

export default MCC;
